import React, { useState } from 'react';

const API_BASE_URL = 'https://api.bringit.co.in/api';

interface OTPAuthProps {
  onSuccess: (token: string, user: any) => void;
}

const OTPAuth: React.FC<OTPAuthProps> = ({ onSuccess }) => {
  const [step, setStep] = useState<'phone' | 'otp' | 'details'>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [userDetails, setUserDetails] = useState({
    name: '',
    email: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Format phone with country code
      let formattedPhone = phone.trim();
      if (!formattedPhone.startsWith('+')) {
        formattedPhone = '+91' + formattedPhone;
      }

      console.log('Sending OTP to:', formattedPhone);
      
      const response = await fetch(`${API_BASE_URL}/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: formattedPhone })
      });

      console.log('Response status:', response.status);
      const result = await response.json();
      console.log('Response data:', result);
      
      if (response.ok && result.success) {
        setPhone(formattedPhone);
        setStep('otp');
        alert('OTP sent to your phone! Check SMS.');
      } else {
        setError(result.message || `Failed to send OTP (${response.status})`);
      }
    } catch (err: any) {
      console.error('OTP send error:', err);
      setError(`Network error: ${err.message || 'Please check your connection'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (otp.length !== 6) {
        setError('Please enter a valid 6-digit OTP');
        setLoading(false);
        return;
      }

      const verifyResponse = await fetch(`${API_BASE_URL}/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: phone, otp })
      });

      const verifyResult = await verifyResponse.json();
      
      if (verifyResult.success) {
        // Check if user exists with this phone
        const loginResponse = await fetch(`${API_BASE_URL}/users/login-phone`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone })
        });
        
        if (loginResponse.ok) {
          // User exists, log them in
          const userData = await loginResponse.json();
          localStorage.setItem('token', userData.token);
          onSuccess(userData.token, userData);
        } else {
          // New user, collect details
          setStep('details');
        }
      } else {
        setError(verifyResult.message || 'Invalid OTP');
      }
    } catch (err: any) {
      setError('Error verifying OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/users/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: userDetails.name,
          email: userDetails.email,
          phone: phone,
          password: phone, // Use phone as password for OTP users
          address: userDetails.address
        })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        onSuccess(data.token, data);
      } else {
        setError(data.message || 'Registration failed');
      }
    } catch (err: any) {
      setError('Error completing registration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logoSection}>
          <img src="/logo-new.png" alt="BringIt" style={styles.logoImage} />
          <p style={styles.subtitle}>Verify with OTP</p>
        </div>

        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        {/* Phone Number Step */}
        {step === 'phone' && (
          <form onSubmit={handleSendOTP} style={styles.form}>
            <h3 style={styles.stepTitle}>Enter Phone Number</h3>
            <div style={styles.phoneInput}>
              <span style={styles.countryCode}>+91</span>
              <input
                type="tel"
                placeholder="10-digit mobile number"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                style={styles.input}
                maxLength={10}
                required
              />
            </div>
            <button type="submit" disabled={loading} style={styles.button}>
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </button>
            <div id="recaptcha-container"></div>
          </form>
        )}

        {/* OTP Verification Step */}
        {step === 'otp' && (
          <form onSubmit={handleVerifyOTP} style={styles.form}>
            <h3 style={styles.stepTitle}>Enter OTP</h3>
            <p style={styles.otpHint}>OTP sent to +91{phone}</p>
            <input
              type="text"
              placeholder="6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              style={styles.input}
              maxLength={6}
              required
            />
            <button type="submit" disabled={loading} style={styles.button}>
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
            <button 
              type="button" 
              onClick={() => setStep('phone')}
              style={styles.backButton}
            >
              Change Number
            </button>
          </form>
        )}

        {/* User Details Step */}
        {step === 'details' && (
          <form onSubmit={handleCompleteRegistration} style={styles.form}>
            <h3 style={styles.stepTitle}>Complete Your Profile</h3>
            <input
              type="text"
              placeholder="Full Name"
              value={userDetails.name}
              onChange={(e) => setUserDetails({...userDetails, name: e.target.value})}
              style={styles.input}
              required
            />
            <input
              type="email"
              placeholder="Email Address"
              value={userDetails.email}
              onChange={(e) => setUserDetails({...userDetails, email: e.target.value})}
              style={styles.input}
              required
            />
            <input
              type="text"
              placeholder="Delivery Address"
              value={userDetails.address}
              onChange={(e) => setUserDetails({...userDetails, address: e.target.value})}
              style={styles.input}
              required
            />
            <button type="submit" disabled={loading} style={styles.button}>
              {loading ? 'Creating Account...' : 'Complete Registration'}
            </button>
          </form>
        )}

        <div style={styles.footer}>
          <p style={styles.footerText}>
            By continuing, you agree to our Terms & Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    padding: '20px'
  },
  card: {
    background: 'white',
    borderRadius: '20px',
    padding: '40px',
    maxWidth: '450px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
  },
  logoSection: {
    textAlign: 'center' as const,
    marginBottom: '30px'
  },
  logo: {
    fontSize: '80px',
    marginBottom: '10px'
  },
  logoImage: {
    width: '180px',
    height: 'auto',
    marginBottom: '20px',
    background: 'white',
    borderRadius: '20px',
    padding: '15px',
    boxShadow: '0 8px 30px rgba(99, 102, 241, 0.4)',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#6366f1',
    margin: '0'
  },
  subtitle: {
    color: '#666',
    marginTop: '5px'
  },
  stepTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '20px',
    textAlign: 'center' as const
  },
  form: {
    marginTop: '20px'
  },
  phoneInput: {
    display: 'flex',
    gap: '10px',
    marginBottom: '15px'
  },
  countryCode: {
    padding: '15px 20px',
    background: '#f0f0f0',
    border: '2px solid #e0e0e0',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#333'
  },
  input: {
    width: '100%',
    padding: '15px 20px',
    fontSize: '16px',
    border: '2px solid #e0e0e0',
    borderRadius: '12px',
    marginBottom: '15px',
    outline: 'none',
    transition: 'border-color 0.3s',
    boxSizing: 'border-box' as const
  },
  button: {
    width: '100%',
    padding: '15px',
    fontSize: '18px',
    fontWeight: 'bold',
    color: 'white',
    background: '#6366f1',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'background 0.3s',
    marginTop: '10px'
  },
  backButton: {
    width: '100%',
    padding: '15px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#6366f1',
    background: 'transparent',
    border: '2px solid #6366f1',
    borderRadius: '12px',
    cursor: 'pointer',
    marginTop: '10px'
  },
  otpHint: {
    textAlign: 'center' as const,
    color: '#666',
    marginBottom: '20px',
    fontSize: '14px'
  },
  error: {
    background: '#fee',
    color: '#c33',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '15px',
    textAlign: 'center' as const
  },
  footer: {
    marginTop: '30px',
    textAlign: 'center' as const
  },
  footerText: {
    fontSize: '12px',
    color: '#999'
  }
};

export default OTPAuth;
